import {config} from 'config/env';
import {
  BasketLineItem,
  BasketRoot,
  EstimatedPointsDetails,
  EventMessagesType,
  OmniChannelVoucher,
  PreviewBasketItemV5,
  PromoAdvice,
  Promotion,
  TBasketPromotionsLiteResponse,
  UpdateBasketItemError,
  UserAddress,
  UserAddressWithOptionalCity
} from 'types';

import ReactNative from './react-native';

declare global {
  interface Window {
    ReactNativeWebView: any;
  }
}

class BrowserEventService {
  origin = config.MAKNET_WEB_BASE_URL;

  getIdToken() {
    this.postMessage(
      {
        type: EventMessagesType.GET_ID_TOKEN
      },
      this.origin
    );
  }

  goToHomePage() {
    this.postMessage(
      {
        type: EventMessagesType.GO_TO_HOME_PAGE
      },
      this.origin
    );
  }

  closeBasketDrawer() {
    this.postMessage(
      {
        type: EventMessagesType.CLOSE_BASKET_DRAWER
      },
      this.origin
    );
  }

  showSuccessToast(_message: string) {
    return;
  }

  showInfoToast(_message: string) {
    return;
  }

  showInfoToastEnabled(_message: string) {
    this.postMessage(
      {
        type: EventMessagesType.SHOW_BASKET_INFO_TOAST,
        message: _message
      },
      this.origin
    );
  }

  showErrorToast(error?: UpdateBasketItemError, message?: string) {
    this.postMessage(
      {
        type: EventMessagesType.SHOW_BASKET_ERROR,
        error,
        message
      },
      this.origin
    );
  }

  goToPDP(productId: string) {
    this.postMessage(
      {
        type: EventMessagesType.GO_TO_PDP,
        productId
      },
      this.origin
    );
  }

  goToBasketVouchers(
    allBasketItems: BasketLineItem[],
    appliedVoucherPromotion: OmniChannelVoucher[] = [],
    availableVouchers = 0,
    basketTotalPrice = 0,
    selectedItemsList: string[] = []
  ) {
    this.postMessage(
      {
        type: EventMessagesType.GO_TO_BASKET_VOUCHERS,
        allBasketItems,
        appliedVoucherPromotion,
        availableVouchers,
        basketTotalPrice,
        selectedItemsList
      },
      this.origin
    );
  }

  goBasketRecommendationToPDP(productId: string, analyticsProps?: any) {
    this.postMessage(
      {
        type: EventMessagesType.RECOMMENDATION_GO_TO_PDP,
        productId,
        analyticsProps
      },
      this.origin
    );
  }

  triggerAddToCartAnalytics = (eventName: string, params?: any) => {
    this.postMessage(
      {
        type: EventMessagesType.TRIGGER_BASKET_ANALYTICS,
        eventName,
        params
      },
      this.origin
    );
  };

  goToPLP(recommendationsPath: string, analyticsProps?: any) {
    this.postMessage(
      {
        type: EventMessagesType.GO_TO_PLP,
        recommendationsPath,
        analyticsProps
      },
      this.origin
    );
  }

  carouselScrollEvent(analyticsProps: any) {
    this.postMessage(
      {
        type: EventMessagesType.CAROUSEL_SCROLL_EVENT,
        analyticsProps
      },
      this.origin
    );
  }

  carouselImpressionEvent(analyticsProps: any) {
    this.postMessage(
      {
        type: EventMessagesType.CAROUSEL_IMPRESSION,
        analyticsProps
      },
      this.origin
    );
  }

  basketContentUpdated(basketItems: PreviewBasketItemV5[]) {
    this.postMessage(
      {
        type: EventMessagesType.BASKET_CONTENT_UPDATED,
        basketItems
      },
      this.origin
    );
  }

  updateBasketPromotions(promotions: TBasketPromotionsLiteResponse) {
    this.postMessage(
      {
        type: EventMessagesType.UPDATE_BASKET_PROMOTIONS,
        promotions
      },
      this.origin
    );
  }

  handleDirectCheckout(address: UserAddressWithOptionalCity) {
    this.postMessage(
      {
        type: EventMessagesType.DIRECT_CHECKOUT,
        address
      },
      this.origin
    );
  }

  goToCheckout(checkoutId: string) {
    this.postMessage(
      {
        type: EventMessagesType.GO_TO_CHECKOUT,
        checkoutId: checkoutId
      },
      this.origin
    );
  }

  updateZipAndSubdistricts(address: UserAddress) {
    this.postMessage(
      {
        type: EventMessagesType.UPDATE_ZIP_AND_SUBDISTRICT,
        address
      },
      this.origin
    );
  }

  updateMetaDataZipAndSubdistricts(address: UserAddress) {
    this.postMessage(
      {
        type: EventMessagesType.UPDATE_META_DATA_ZIP_AND_SUBDISTRICT,
        address
      },
      this.origin
    );
  }

  onAlcoTermsAndConditionsPress() {
    this.postMessage(
      {
        type: EventMessagesType.ALCO_TERMS_AND_CONDITIONS_PRESS
      },
      this.origin
    );
  }

  toggleProPointsEstimation(estimatedPointsDetails: EstimatedPointsDetails[]) {
    this.postMessage(
      {
        type: EventMessagesType.SHOW_PRO_POINTS_ESTIMATIONS_MODAL,
        estimatedPointsDetails
      },
      this.origin
    );
  }

  goToBasketProPointPromotions() {
    this.postMessage(
      {
        type: EventMessagesType.GO_TO_PRO_POINTS_PROMOTIONS_PAGE
      },
      this.origin
    );
  }

  openProPointPromotionDetailModal(promotion: Promotion | null) {
    this.postMessage(
      {
        type: EventMessagesType.SHOW_PRO_POINTS_PROMOTION_DETAILS_MODAL,
        promotion
      },
      this.origin
    );
  }

  openProPointPromotionActivateNowModal() {
    this.postMessage(
      {
        type: EventMessagesType.SHOW_PRO_POINTS_PROMOTION_ACTIVATE_NOW_MODAL
      },
      this.origin
    );
  }

  openComplextPromotionDetailModal(rm3Promotions: PromoAdvice[], minimumDate: number) {
    this.postMessage(
      {
        type: EventMessagesType.SHOW_COMPLEX_PROMOTION_DETAILS_MODAL,
        rm3Promotions,
        minimumDate
      },
      this.origin
    );
  }

  openHowToGetEcodeModal() {
    this.postMessage(
      {
        type: EventMessagesType.SHOW_HOW_TO_GET_ECODE_MODAL
      },
      this.origin
    );
  }

  showActivateEcodeSuccessToast() {
    this.postMessage(
      {
        type: EventMessagesType.SHOW_ECODE_ACTIVATION_SUCCESS_TOAST
      },
      this.origin
    );
  }

  basketRefreshed(data: BasketRoot) {
    if (!ReactNative.isReactNativeWebView()) {
      return;
    }
    this.postMessage(
      {
        type: EventMessagesType.BASKET_REFRESHED,
        data
      },
      this.origin
    );
  }

  storeBasketItem(storeBasketItemData: any) {
    this.postMessage(
      {
        type: EventMessagesType.ADD_BASKET_ITEM_TO_LOCAL_STORAGE,
        storeBasketItemData
      },
      this.origin
    );
  }

  private postMessage(data: any, origin: string) {
    if (ReactNative.isReactNativeWebView()) {
      const view = window.ReactNativeWebView as Window;
      view.postMessage(JSON.stringify(data));
    } else {
      window.parent.postMessage(data, origin);
    }
  }
}

const SendBrowserEvent = new BrowserEventService();

export default SendBrowserEvent;
